import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  isChunkLoadError,
  reloadAfterDeployOnce,
} from "../lib/chunk-load-error";
import { Button } from "./button";
import { Card, CardContent } from "./card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const staleDeploy = isChunkLoadError(this.state.error);

      return (
        <Card className="mx-auto mt-8 max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="bg-destructive/10 flex h-12 w-12 items-center justify-center rounded-full">
              <AlertTriangle className="text-destructive h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">
                {staleDeploy
                  ? "Mise à jour disponible"
                  : "Une erreur est survenue"}
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {staleDeploy
                  ? "L'application a été mise à jour. Rechargez la page pour continuer."
                  : this.state.error?.message || "Erreur inattendue"}
              </p>
            </div>
            <Button
              onClick={() => {
                if (staleDeploy) {
                  reloadAfterDeployOnce();
                  return;
                }
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {staleDeploy ? "Mettre à jour" : "Recharger"}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
